import useClientPagination from 'hooks/use-client-pagination'
import UrlPagination from 'components/molecules/url-pagination'
import AdminsApiV2 from 'api/v2/admins-api-v2'
import AdministratorsApiV2 from 'api/v2/admins/administrators-api-v2'
import { errorMessage } from 'domains/dashboard-migration/api'
import TitlePage from 'components/atoms/title-page'
import { ALERT_TYPES } from 'constants/alert-constants'
import PropTypes from 'prop-types'
import React, { useCallback, useEffect, useRef, useState } from 'react'
import {
  Badge,
  Button,
  Card,
  CardBody,
  Col,
  FormGroup,
  Input,
  Label,
  Modal,
  ModalBody,
  ModalFooter,
  ModalHeader,
  Row,
  Table
} from 'reactstrap'

const propTypes = {
  pageUtils: PropTypes.shape({
    setAlertMsg    : PropTypes.func,
    setApiErrorMsg : PropTypes.func
  })
}

const ROLE_OPTIONS = [ 'Owner', 'Admin', 'Finance', 'Warehouse', 'Sales', 'Purchasing', 'Marketing', 'Web', 'Stc', 'Customer Service', 'product_curated', 'HR' ]
const emptyValues = { email: '', first_name: '', last_name: '', password: '', password_confirmation: '', role: '', active: true }

const AdministratorPage = ({ pageUtils }) => {
  const [ administrators, setAdministrators ] = useState([])
  const [ currentAdminId, setCurrentAdminId ] = useState(null)
  const [ query, setQuery ] = useState('')
  const [ role, setRole ] = useState('all')
  const [ active, setActive ] = useState('')
  const [ error, setError ] = useState('')
  const requestSequence = useRef(0)
  const saving = useRef(false)
  const [ selected, setSelected ] = useState(null)
  const [ values, setValues ] = useState(emptyValues)
  const [ isModalOpen, setIsModalOpen ] = useState(false)
  const [ isLoading, setIsLoading ] = useState(false)
  const [ isSaving, setIsSaving ] = useState(false)

  const changeValue = (name, value) => setValues((current) => ({ ...current, [name]: value }))
  const showError = useCallback(async (err) => setError(await errorMessage(err)), [])

  const fetchAdministrators = useCallback((filters = { q: query, role, active }) => {
    const sequence = ++requestSequence.current
    setError('')
    setIsLoading(true)
    AdministratorsApiV2.get(filters)
      .then((response) => { if (sequence === requestSequence.current) setAdministrators(response.data.administrators || []) })
      .catch(showError)
      .finally(() => { if (sequence === requestSequence.current) setIsLoading(false) })
  }, [ query, role, active, showError ])

  useEffect(() => {
    fetchAdministrators({ q: '', role: 'all' })
    AdminsApiV2.profile()
      .then((response) => setCurrentAdminId(response.data.admin?.id || null))
      .catch(() => setCurrentAdminId(null))
  }, []) // eslint-disable-line react-hooks/exhaustive-deps

  const closeModal = () => {
    if (saving.current) return
    setError('')
    setIsModalOpen(false)
    setSelected(null)
    setValues({ ...emptyValues })
  }

  const openCreate = () => {
    setError('')
    setSelected(null)
    setValues({ ...emptyValues })
    setIsModalOpen(true)
  }

  const openEdit = (administrator) => {
    setError('')
    setSelected(administrator)
    setValues({
      email                 : administrator.email || '',
      first_name            : administrator.first_name || '',
      last_name             : administrator.last_name || '',
      password              : '',
      password_confirmation : '',
      role                  : administrator.role || '',
      active                : administrator.active !== false
    })
    setIsModalOpen(true)
  }

  const handleFilter = (event) => {
    listPagination.setPage(1)
    event.preventDefault()
    fetchAdministrators({ q: query.trim(), role, active })
  }

  const handleSubmit = (event) => {
    event.preventDefault()
    if (saving.current) return
    saving.current = true
    setError('')
    setIsSaving(true)
    const request = selected ? AdministratorsApiV2.update(selected.id, values) : AdministratorsApiV2.create(values)

    request
      .then(() => {
        pageUtils?.setAlertMsg?.(`Administrator has been ${selected ? 'updated' : 'created'}`, ALERT_TYPES.SUCCESS)
        saving.current = false
        closeModal()
        fetchAdministrators({ q: query.trim(), role, active })
      })
      .catch(showError)
      .finally(() => { saving.current = false; setIsSaving(false) })
  }

  const handleDelete = (administrator) => {
    if (administrator.id === currentAdminId) return
    if (!window.confirm(`Deactivate administrator ${administrator.email}? Login will be blocked; history will be retained.`)) return

    AdministratorsApiV2.destroy(administrator.id)
      .then(() => {
        pageUtils?.setAlertMsg?.('Administrator has been deactivated; history retained', ALERT_TYPES.SUCCESS)
        fetchAdministrators({ q: query.trim(), role, active })
      })
      .catch(showError)
  }

  const downloadExport = (format) => {
    AdministratorsApiV2.export({ format, q: query.trim(), role, active })
      .then((response) => {
        const url = window.URL.createObjectURL(new Blob([ response.data ]))
        const anchor = document.createElement('a')
        anchor.href = url
        anchor.download = `administrators.${format}`
        document.body.appendChild(anchor)
        anchor.click()
        anchor.remove()
        window.URL.revokeObjectURL(url)
      })
      .catch(showError)
  }

  const listPagination = useClientPagination(administrators, { loading: isLoading })

  return (
    <>
      <TitlePage mainTitle='Setting' subTitle='Administrators' />
      { error && !isModalOpen && <div role='alert' className='alert alert-danger'>{ error }</div> }
      <Card>
        <CardBody>
          <form onSubmit={ handleFilter }>
            <Row className='mb-3 align-items-center'>
              <Col md={ 4 }><Input value={ query } onChange={ (event) => { listPagination.setPage(1); setQuery(event.target.value) } } placeholder='Search name / email' /></Col>
              <Col md={ 8 } className='text-md-right mt-2 mt-md-0'>
                <Button type='submit' color='primary' className='mr-2'>{ 'Search' }</Button>
                <Button type='button' color='light' className='mr-2' onClick={ () => downloadExport('csv') }>{ 'Export CSV' }</Button>
                <Button type='button' color='light' className='mr-2' onClick={ () => downloadExport('xlsx') }>{ 'Export XLSX' }</Button>
                <Button type='button' color='primary' onClick={ openCreate }>{ 'Create Administrator' }</Button>
              </Col>
            </Row>
          </form>

          <Table hover responsive>
            <thead><tr><th>{ 'Email' }</th><th>{ 'First Name' }</th><th>{ 'Last Name' }</th><th>{ 'Role' }<Input type='select' bsSize='sm' aria-label='Filter role' value={ role } onChange={ (event) => { const value = event.target.value; listPagination.setPage(1); setRole(value); fetchAdministrators({ q: query.trim(), role: value, active }) } }><option value='all'>All Roles</option>{ ROLE_OPTIONS.map((option) => <option key={ option } value={ option }>{ option }</option>) }</Input></th><th>{ 'Active' }<Input type='select' bsSize='sm' aria-label='Filter active status' value={ active } onChange={ (event) => { const value = event.target.value; listPagination.setPage(1); setActive(value); fetchAdministrators({ q: query.trim(), role, active: value }) } }><option value=''>All statuses</option><option value='true'>Active</option><option value='false'>Inactive</option></Input></th><th>{ 'Action' }</th></tr></thead>
            <tbody>
              { !isLoading && listPagination.rows.map((administrator) => (
                <tr key={ administrator.id }>
                  <td>{ administrator.email || '-' }</td>
                  <td>{ administrator.first_name || '-' }</td>
                  <td>{ administrator.last_name || '-' }</td>
                  <td>{ administrator.role || '-' }</td>
                  <td>{ administrator.active ? <Badge color='success'>{ 'Active' }</Badge> : <Badge color='danger'>{ 'Inactive' }</Badge> }</td>
                  <td className='text-nowrap'>
                    <Button color='info' size='sm' className='mr-2' onClick={ () => openEdit(administrator) }>{ 'Edit' }</Button>
                    { administrator.active && administrator.id !== currentAdminId && <Button color='warning' size='sm' onClick={ () => handleDelete(administrator) }>{ 'Deactivate' }</Button> }
                  </td>
                </tr>
              )) }
              { !isLoading && administrators.length === 0 && <tr><td colSpan='6' className='text-center text-muted'>{ 'No data' }</td></tr> }
              { isLoading && <tr><td colSpan='6' className='text-center'>{ 'Loading...' }</td></tr> }
            </tbody>
          </Table>
          <UrlPagination pagination={ listPagination } loading={ isLoading } />
        </CardBody>
      </Card>

      <Modal isOpen={ isModalOpen } toggle={ closeModal }>
        <form onSubmit={ handleSubmit }>
          <ModalHeader toggle={ closeModal }>{ `${selected ? 'Edit' : 'Create'} Administrator` }</ModalHeader>
          <ModalBody>
            { error && <div role='alert' className='alert alert-danger'>{ error }</div> }
            <FormGroup><Label>{ 'Email' }</Label><Input type='email' required value={ values.email } onChange={ (event) => changeValue('email', event.target.value) } /></FormGroup>
            <Row><Col md={ 6 }><FormGroup><Label>{ 'First Name' }</Label><Input value={ values.first_name } onChange={ (event) => changeValue('first_name', event.target.value) } /></FormGroup></Col><Col md={ 6 }><FormGroup><Label>{ 'Last Name' }</Label><Input value={ values.last_name } onChange={ (event) => changeValue('last_name', event.target.value) } /></FormGroup></Col></Row>
            <Row>
              <Col md={ 6 }><FormGroup><Label>{ selected ? 'New Password (optional)' : 'Password' }</Label><Input type='password' autoComplete='new-password' required={ !selected } value={ values.password } onChange={ (event) => changeValue('password', event.target.value) } /></FormGroup></Col>
              <Col md={ 6 }><FormGroup><Label>{ 'Password Confirmation' }</Label><Input type='password' autoComplete='new-password' required={ !selected } value={ values.password_confirmation } onChange={ (event) => changeValue('password_confirmation', event.target.value) } /></FormGroup></Col>
            </Row>
            <FormGroup><Label>{ 'Role' }</Label><Input type='select' required value={ values.role } onChange={ (event) => changeValue('role', event.target.value) }><option value=''>{ 'Select role' }</option>{ ROLE_OPTIONS.map((option) => <option key={ option } value={ option }>{ option }</option>) }</Input></FormGroup>
            <FormGroup check><Label check><Input type='checkbox' disabled={ selected?.id === currentAdminId } checked={ values.active } onChange={ (event) => changeValue('active', event.target.checked) } />{ ' Active' }</Label></FormGroup>
          </ModalBody>
          <ModalFooter><Button type='button' color='secondary' onClick={ closeModal } disabled={ isSaving }>{ 'Cancel' }</Button><Button type='submit' color='primary' disabled={ isSaving }>{ isSaving ? 'Saving...' : 'Save' }</Button></ModalFooter>
        </form>
      </Modal>
    </>
  )
}

AdministratorPage.propTypes = propTypes
export default AdministratorPage
